import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * How far ahead of the viewport a tile counts as approaching: two screen
 * heights below the fold. Below only — the reader scrolling back up is
 * returning to pictures the browser has already cached.
 */
const APPROACH_MARGIN = '0px 0px 200% 0px';

// One observer for every tile on the page rather than one each: the callback
// fires with whichever tiles crossed the line together, and a listing holds
// hundreds of tiles at once.
let approachObserver: IntersectionObserver | null = null;
const onApproach = new WeakMap<Element, () => void>();

const getApproachObserver = () => {
    approachObserver ??= new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                const notify = onApproach.get(entry.target);
                // One-way: an approached tile never demotes, so it can stop
                // being watched the moment it crosses.
                approachObserver?.unobserve(entry.target);
                onApproach.delete(entry.target);
                notify?.();
            }
        },
        { rootMargin: APPROACH_MARGIN },
    );
    return approachObserver;
};

/**
 * Whether the element has come within two viewports of the fold.
 *
 * For promoting a lazy tile picture to an early low-priority fetch, so it is
 * decoded before the reader reaches it: native lazy loading starts fetching
 * on a distance the browser picks, and a scroll at reading pace outruns it.
 * Answers false until the element approaches, then true for good.
 *
 * Stays false — leaving the picture to native lazy loading — when the reader
 * has asked to save data, and on the server, where there is nothing to watch.
 */
export const useApproachPreload = (
    ref: RefObject<Element | null>,
    enabled: boolean,
): boolean => {
    const [approached, setApproached] = useState(false);

    useEffect(() => {
        if (!enabled || approached) return;
        const element = ref.current;
        if (!element) return;
        const connection = (
            navigator as Navigator & { connection?: { saveData?: boolean } }
        ).connection;
        if (connection?.saveData) return;

        const observer = getApproachObserver();
        onApproach.set(element, () => setApproached(true));
        observer.observe(element);
        return () => {
            onApproach.delete(element);
            observer.unobserve(element);
        };
    }, [enabled, approached, ref]);

    return approached;
};
